import { useEffect, useMemo, useState } from 'react';
import Modal from '@/components/base/Modal';
import { useAppStore } from '@/store/AppStore';
import { EXPENSE_CATEGORIES } from '@/mocks/constants';
import type { Expense } from '@/types';
import { toTrDate, parseTrDate } from '@/lib/date';

interface ExpenseFormProps {
  open: boolean;
  onClose: () => void;
}

const inputClass =
  'h-10 w-full rounded-md border border-background-200 bg-white px-3 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100';
const labelClass = 'mb-1.5 block text-xs font-semibold text-foreground-600';

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default function ExpenseForm({ open, onClose }: ExpenseFormProps) {
  const { jobs, teams, addExpense } = useAppStore();
  const [date, setDate] = useState(toTrDate(new Date()));
  const [category, setCategory] = useState(EXPENSE_CATEGORIES[0]);
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [jobId, setJobId] = useState('');
  const [team, setTeam] = useState('');
  const [error, setError] = useState('');

  const dateIso = useMemo(() => {
    const d = parseTrDate(date);
    return toIsoDate(d);
  }, [date]);

  function reset() {
    setDate(toTrDate(new Date()));
    setCategory(EXPENSE_CATEGORIES[0]);
    setAmount('');
    setDescription('');
    setJobId('');
    setTeam('');
    setError('');
  }

  useEffect(() => {
    if (open) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const numericAmount = Number(amount);
    if (!numericAmount || numericAmount <= 0) {
      setError('Geçerli bir tutar girilmelidir.');
      return;
    }
    const expense: Expense = {
      id: `EX-2026-${String(Date.now()).slice(-4)}`,
      date,
      category,
      amount: numericAmount,
      description,
      jobId,
      team,
    };
    addExpense(expense);
    reset();
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Gider Ekle"
      maxWidth="max-w-xl"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-background-200 px-4 py-2 text-sm font-semibold text-foreground-700 hover:bg-background-100"
          >
            Vazgeç
          </button>
          <button
            type="submit"
            form="expense-form"
            className="rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-600"
          >
            Kaydet
          </button>
        </>
      }
    >
      <form id="expense-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className={labelClass}>Tarih</label>
            <input
              type="date"
              className={inputClass}
              value={dateIso}
              onChange={(e) => {
                const iso = e.target.value;
                if (iso) {
                  const d = new Date(iso);
                  setDate(toTrDate(d));
                }
              }}
            />
          </div>
          <div>
            <label className={labelClass}>Kategori</label>
            <select className={inputClass} value={category} onChange={(e) => setCategory(e.target.value)}>
              {EXPENSE_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Tutar (TL)</label>
            <input
              className={inputClass}
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0"
            />
          </div>
          <div>
            <label className={labelClass}>İlgili İş (opsiyonel)</label>
            <select className={inputClass} value={jobId} onChange={(e) => setJobId(e.target.value)}>
              <option value="">Seçilmedi</option>
              {jobs.map((j) => (
                <option key={j.id} value={j.id}>
                  {j.id} — {j.customerName}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Ekip (opsiyonel)</label>
            <select className={inputClass} value={team} onChange={(e) => setTeam(e.target.value)}>
              <option value="">Seçilmedi</option>
              {teams.map((t) => (
                <option key={t.id} value={t.name}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className={labelClass}>Açıklama</label>
          <input
            className={inputClass}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Gider açıklaması"
          />
        </div>
        {error && (
          <p className="rounded-md bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">{error}</p>
        )}
      </form>
    </Modal>
  );
}